import { useAuthStore } from '@caradasstore/AuthStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useUserStore } from '@caradasstore/UserStore'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { useAvailabilityStore } from '@caradasstore/AvailabilityStore'
import useAdasIntelligenceStore from '@caradasstore/AdasIntelligenceStore'
import loginLogo from '../../assets/images/adas-intelligence-login-logo.svg'
import { User, Lock, Eye, EyeOff, Loader2, UserPlus, ArrowRight } from 'lucide-react'
import React, { useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { getCalibrationCenterList, getUserIP, getUserList, requestAuth, requestPasswordReset } from '../../api/api'
import { setCachedCalibrationCenters, clearCachedCalibrationCenters } from '../../utils/calibrationCenterCache'
import { Logger } from '../../logger'
import { validations } from '../../utils/utils'
import { datadogRum } from '@datadog/browser-rum'

export const AuthForm = () => {
  const history = useHistory()
  const emailInputRef = useRef()
  const passwordInputRef = useRef()
  const location = useLocation()
  const { from } = location.state || { from: { pathname: '/' } }
  const setCcId = useCenterStore((store) => store.setCcId)
  const setAccessibleCcIds = useCenterStore((store) => store.setAccessibleCcIds)
  const setIsCenterAdmin = useCenterStore((store) => store.setIsCenterAdmin)
  const currentUser = useUserStore((store) => store.currentUser)
  const setCurrentUser = useUserStore((store) => store.setCurrentUser)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const authCtx = useAuthStore()
  const [isLogin, setIsLogin] = useState(true)
  const [isEmailError, setIsEmailError] = useState(false)
  const [isPasswordError, setIsPasswordError] = useState(false)
  const [isLoginFailed, setIsLoginFailed] = useState(false)
  const [loginFailureMessage, setLoginFailureMessage] = useState(false)
  const [isPasswordResetSuccess, setIsPasswordResetSuccess] = useState(false)
  const [isPasswordResetFailed, setIsPasswordResetFailed] = useState(false)
  const [showPassword, setShowPassword] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const setInitialState = () => {
    setIsEmailError(false)
    setIsPasswordResetSuccess(false)
    setIsPasswordResetFailed(false)
    setIsLoginFailed(false)
  }

  const switchAuthModeHandler = () => {
    setIsLogin((prevState) => !prevState)
    setInitialState()
    if (emailInputRef.current) emailInputRef.current.value = ''
  }

  // Fetch the full calibration-center list once at login and stash it in session storage.
  // The navbar location dropdown reads from this cache; it's cleared on logout/signout.
  // ADMIN ONLY (CAR-3670): GET /v1/calibrationcenters is an admin-only endpoint. Calling it
  // as a non-admin 403s, and the global auth interceptor redirects to /forbidden mid-login.
  // Non-admins don't need this cache — CalibrationCenterBar hydrates their scoped list
  // per-ID from accessibleCcIds instead.
  const cacheCalibrationCenters = async () => {
    try {
      const response = await getCalibrationCenterList({ pageNo: 0, pageSize: 500, customErrorHandling: true })
      if (response?.status === 200) {
        setCachedCalibrationCenters(response.data?.calibrationCenters || [])
      }
    } catch (error) {
      Logger.error({ message: error.message, payload: { path: 'AuthForm/cacheCalibrationCenters' } })
    }
  }

  const fetchUserList = async (ccId, userEmail) => {
    try {
      const response = await getUserList({ ccid: ccId })
      if (response?.status === 200) {
        const user = response?.data?.find((item) => item.email?.toLowerCase() === userEmail?.toLowerCase())
        if (user) {
          const userIP = await getUserIP()
          datadogRum.setUser({ id: user.id, name: user.name, email: user.email, ip: userIP })
          setCurrentUser(user)
        }
      }
    } catch (error) {
      Logger.error({ message: error.message, payload: { ccId, path: 'AuthForm/fetchUserList' } })
    }
  }

  // Wipe the previous account's persisted per-account state before applying the new
  // session. Login only *overwrites* these stores, and setCurrentUser runs only when
  // getUserList finds the user — so a stale admin currentUser/isCenterAdmin from an earlier
  // login on the same browser could survive and make a non-admin fire admin-only endpoints
  // (e.g. GET /v1/calibrationcenters → 403 → /forbidden). Mirrors the interceptor logout path.
  const clearPreviousAccountState = () => {
    useCenterStore.getState().reset?.()
    useUserStore.getState().reset?.()
    useOrganisationStore.getState().reset?.()
    useAvailabilityStore.getState().reset?.()
    useAdasIntelligenceStore.getState().reset?.()
    clearCachedCalibrationCenters()
  }

  const submitHandler = async (event) => {
    event.preventDefault()
    setInitialState()
    setIsSubmitting(true)

    if (isLogin) {
      const enteredEmail = emailInputRef.current.value
      const enteredPassword = passwordInputRef.current.value

      // Native input[type=email] validity accepts addresses with no TLD (e.g. name@name),
      // so fall back to the app-wide regex, which requires a full domain (CAR-3806).
      if (!emailInputRef.current.validity.valid || !validations.email(enteredEmail)) {
        setIsEmailError(true)
        setIsSubmitting(false)
        return
      }
      setIsEmailError(false)
      if (!passwordInputRef.current.validity.valid) {
        setIsPasswordError(true)
        setIsSubmitting(false)
        return
      }
      setIsPasswordError(false)

      try {
        const authResponse = await requestAuth({ email: enteredEmail, password: enteredPassword })
        if (authResponse && authResponse.status === 200) {
          setLoading(false)
          setIsLoginFailed(false)
          clearPreviousAccountState()
          setCcId(authResponse.data?.ccid)
          const accessibleIds = [
            ...(authResponse.data?.ccidOwned || []),
            ...(authResponse.data?.ccidManaged || []),
            ...(authResponse.data?.ccidTech || [])
          ]
          if (authResponse.data?.ccid != null) accessibleIds.push(authResponse.data.ccid)
          setAccessibleCcIds(accessibleIds)
          setIsCenterAdmin(!!authResponse.data?.isAdmin)
          authCtx.login(authResponse.data?.token, authResponse.data?.refreshToken)
          const postLoginTasks = [fetchUserList(authResponse.data?.ccid, emailInputRef.current.value)]
          if (authResponse.data?.isAdmin) postLoginTasks.push(cacheCalibrationCenters())
          await Promise.all(postLoginTasks)
          history.replace(from)
        } else {
          setIsLoginFailed(true)
        }
      } catch (error) {
        setIsLoginFailed(true)
        setLoginFailureMessage(error?.response?.data?.message)
        Logger.error({ message: error.message, payload: { path: 'AuthForm/submitHandler', email: enteredEmail, isLogin } })
      }
    } else {
      const enteredEmail = emailInputRef.current.value

      if (!emailInputRef.current.validity.valid || !validations.email(enteredEmail)) {
        setIsEmailError(true)
        setIsSubmitting(false)
        return
      }
      setIsEmailError(false)

      try {
        const response = await requestPasswordReset({ email: enteredEmail })
        if (response.status === 200) {
          setIsPasswordResetSuccess(true)
          setIsPasswordResetFailed(false)
        } else {
          setIsPasswordResetSuccess(false)
          setIsPasswordResetFailed(true)
        }
      } catch (error) {
        setIsPasswordResetSuccess(false)
        setIsPasswordResetFailed(true)
        Logger.error({ message: error.message, payload: { path: 'AuthForm/submitHandler', email: enteredEmail, isLogin } })
      }
    }
    setIsSubmitting(false)
  }

  const inputClass =
    'w-full h-12 rounded-full bg-white/[0.12] backdrop-blur-sm border border-white/[0.15] pl-12 pr-4 text-white text-sm placeholder:text-white/50 outline-none focus:border-white/40 focus:bg-white/[0.18] transition-colors'
  const iconClass = 'absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/60'

  return (
    <div
      className='flex min-h-screen w-full items-center justify-center'
      style={{
        background: 'linear-gradient(135deg, #0A1628 0%, #0F2035 40%, #132B45 70%, #0A1628 100%)',
        backgroundSize: 'cover'
      }}
    >
      {/* Subtle hex pattern overlay */}
      <div
        className='pointer-events-none fixed inset-0 opacity-[0.06]'
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100' viewBox='0 0 56 100'%3E%3Cpath d='M28 66L0 50L0 16L28 0L56 16L56 50L28 66ZM28 100L0 84L0 50L28 34L56 50L56 84L28 100Z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      <div className='relative z-10 w-full max-w-[380px] px-4'>
        {/* Glass card */}
        <div className='rounded-2xl border border-white/[0.08] bg-white/[0.04] p-8 shadow-2xl backdrop-blur-md'>
          {/* Logo + Title */}
          <div className='mb-8 flex flex-col items-center'>
            <img src={loginLogo} alt='ADAS Intelligence' width={220} style={{ height: 'auto', display: 'block' }} />
          </div>

          {!isPasswordResetSuccess ? (
            <form onSubmit={submitHandler} className='space-y-4'>
              {/* Username / Email */}
              <div className='relative'>
                <User className={iconClass} />
                <input
                  ref={emailInputRef}
                  type='email'
                  required
                  placeholder={isLogin ? 'Username' : 'Email'}
                  className={`${inputClass} ${isEmailError ? 'border-red-400/60' : ''}`}
                  autoComplete='email'
                />
                {isEmailError && <div className='mt-1 pl-4 text-[11px] text-red-300'>Please enter a valid email address</div>}
              </div>

              {/* Password (login only) */}
              {isLogin && (
                <div className='relative'>
                  <Lock className={iconClass} />
                  <input
                    ref={passwordInputRef}
                    type={showPassword ? 'text' : 'password'}
                    required
                    placeholder='Password'
                    className={`${inputClass} pr-12 ${isPasswordError ? 'border-red-400/60' : ''}`}
                    autoComplete='current-password'
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors'
                    aria-label='Toggle password visibility'
                  >
                    {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                  </button>
                  {isPasswordError && <div className='mt-1 pl-4 text-[11px] text-red-300'>Password is required</div>}
                </div>
              )}

              {/* Submit */}
              <button
                type='submit'
                disabled={isSubmitting}
                className='mt-2 flex h-12 w-full items-center justify-center rounded-full bg-gradient-to-r from-[#1F6FEB] to-[#1F6FEB] text-sm font-bold uppercase tracking-widest text-white shadow-lg shadow-blue-500/25 transition-all hover:shadow-blue-500/40 hover:brightness-110 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none'
              >
                {isSubmitting ? <Loader2 className='h-5 w-5 animate-spin' /> : isLogin ? 'LOG IN' : 'Send Reset Link'}
              </button>

              {/* Forgot password link */}
              <div className='text-center'>
                <button
                  type='button'
                  onClick={switchAuthModeHandler}
                  className='text-[12px] text-white/50 hover:text-white/80 transition-colors'
                >
                  {isLogin ? 'Forgot password?' : 'Back to Sign In'}
                </button>
              </div>
              {/* Error messages */}
              {isLoginFailed && (
                <div className='rounded-lg bg-red-500/10 border border-red-400/20 px-4 py-2 text-center text-[12px] text-red-300'>
                  {loginFailureMessage || 'Email and password do not match. Please try again.'}
                </div>
              )}
              {isPasswordResetFailed && (
                <div className='rounded-lg bg-red-500/10 border border-red-400/20 px-4 py-2 text-center text-[12px] text-red-300'>
                  Password reset request failed. Please try again.
                </div>
              )}

              {/* Self-serve sign-up: its own section, a full-width button second only to Log in. */}
              {isLogin && (
                <div className='pt-2'>
                  <div className='mb-4 flex items-center gap-3' aria-hidden='true'>
                    <span className='h-px flex-1 bg-white/[0.12]' />
                    <span className='text-[11px] font-semibold uppercase tracking-widest text-white/45'>New to ADAS Intelligence?</span>
                    <span className='h-px flex-1 bg-white/[0.12]' />
                  </div>
                  <button
                    type='button'
                    onClick={() => history.push('/register')}
                    className='group flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#1F6FEB]/70 bg-[#1F6FEB]/[0.12] text-sm font-bold uppercase tracking-widest text-white transition-all hover:border-[#1F6FEB] hover:bg-[#1F6FEB]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F6FEB]/60 active:scale-[0.98]'
                  >
                    <UserPlus className='h-4 w-4' aria-hidden='true' />
                    Create an account
                    <ArrowRight className='h-4 w-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                  </button>
                  <p className='mt-2 text-center text-[11px] text-white/45'>Set up your calibration center in a few minutes.</p>
                </div>
              )}
            </form>
          ) : (
            <div className='text-center'>
              <div className='mb-4 text-sm text-white/80'>
                Password reset link has been sent to{' '}
                <span className='font-semibold text-white'>{currentUser?.userEmail || 'your email'}</span>.
              </div>
              <button
                type='button'
                onClick={switchAuthModeHandler}
                className='text-[13px] font-medium text-[#60A5FA] hover:text-[#93C5FD] transition-colors'
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
